import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Settings2, Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { subjectsOf } from "@/lib/constants";
import { SubjectStylePicker } from "@/components/app/SubjectStylePicker";

const Row = ({ name, onRename, onDelete, busy }) => {
  const [edit, setEdit] = useState(false);
  const [value, setValue] = useState(name);
  const [confirm, setConfirm] = useState(false);
  const locked = name === "Autre";
  if (edit) return (
    <form className="flex items-center gap-2" onSubmit={async (e) => { e.preventDefault(); if (await onRename(name, value)) setEdit(false); }}>
      <input autoFocus data-testid={`subject-rename-input-${name}`} value={value} onChange={(e) => setValue(e.target.value)} maxLength={40}
        className="h-10 flex-1 rounded-xl border bg-background px-3 text-sm outline-none focus:border-primary/50" />
      <button data-testid={`subject-rename-save-${name}`} disabled={busy} className="bg-brand inline-flex h-9 w-9 items-center justify-center rounded-full text-white"><Check className="h-4 w-4" /></button>
      <button type="button" onClick={() => { setEdit(false); setValue(name); }} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"><X className="h-4 w-4" /></button>
    </form>
  );
  return (
    <div className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-muted/60" data-testid={`subject-row-${name}`}>
      <SubjectStylePicker name={name} />
      <span className="flex-1 truncate text-sm font-medium">{name}</span>
      {locked ? <span className="text-xs text-muted-foreground">par défaut</span> : confirm ? (
        <span className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">Supprimer ?</span>
          <button data-testid={`subject-delete-confirm-${name}`} disabled={busy} onClick={() => onDelete(name)} className="rounded-full bg-destructive px-2.5 py-1 font-semibold text-destructive-foreground">Oui</button>
          <button onClick={() => setConfirm(false)} className="rounded-full px-2 py-1 hover:bg-muted">Non</button>
        </span>
      ) : (
        <>
          <button data-testid={`subject-rename-${name}`} aria-label="Renommer" onClick={() => setEdit(true)} className="rounded-full p-1.5 text-muted-foreground hover:bg-card hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`subject-delete-${name}`} aria-label="Supprimer" onClick={() => setConfirm(true)} className="rounded-full p-1.5 text-muted-foreground hover:bg-card hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
        </>
      )}
    </div>
  );
};

export const SubjectManager = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); setUser(data); toast.success(msg); return true; } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/subjects", { name }), `Matière « ${name.trim()} » ajoutée.`)) setName("");
  };
  const syncFilter = (from, to) => {
    if (params.get("matiere") !== from) return;
    if (to) params.set("matiere", to); else params.delete("matiere");
    setParams(params);
  };
  const rename = async (old, value) => {
    if (value.trim() === old) return true;
    const ok = await run(() => api.patch("/subjects", { old, new: value }), "Matière renommée.");
    if (ok) syncFilter(old, value.trim().replace(/\s+/g, " "));
    return ok;
  };
  const remove = async (s) => {
    if (await run(() => api.delete(`/subjects/${encodeURIComponent(s)}`), `Matière « ${s} » supprimée. Ses cours sont rangés dans « Autre ».`)) syncFilter(s, null);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="manage-subjects-button" aria-label="Gérer mes matières" className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><Settings2 className="h-3.5 w-3.5" /></button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="manage-subjects-dialog">
        <DialogHeader>
          <DialogTitle>Gérer mes matières</DialogTitle>
          <DialogDescription>Renomme, ajoute ou supprime tes matières, et touche l'icône d'une matière pour choisir sa couleur et son icône. Les cours d'une matière supprimée sont rangés dans « Autre ».</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <input data-testid="new-subject-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Ex. : Philosophie, SES, Espagnol…"
            className="h-11 flex-1 rounded-full border bg-background px-4 text-sm outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/10" />
          <button data-testid="add-subject-button" disabled={!name.trim() || busy} className="bg-brand inline-flex h-11 items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ajouter
          </button>
        </form>
        <div className="max-h-[50vh] space-y-0.5 overflow-y-auto">
          {subjectsOf(user).map((s) => <Row key={s} name={s} onRename={rename} onDelete={remove} busy={busy} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
